import {
  noop,
  queryOptions,
  useMutation,
  useQuery,
} from "@tanstack/react-query";
import { type StaticDecode, Type } from "typebox";
import * as TypeCompiler from "typebox/compile";
import { consumeReturnTo } from "@/shared/bootstrap/bootRedirect.ts";
import { Nullable, ReadonlyObject } from "@/shared/schema/schema.ts";
import { http, httpChecked } from "@/shared/util/fetch.ts";
import { queryClient } from "@/shared/util/queryClient.tsx";
import { redirectTo } from "@/shared/util/routes.ts";

const transformEpoch = Nullable(
  Type.Codec(Type.Number())
    .Decode((value) => new Date(value))
    .Encode((value) => value.getTime()),
);

const schema = ReadonlyObject({
  enabled: Type.Boolean(),
  notice: Type.Codec(Nullable(Type.String()))
    .Decode((value) => value ?? "")
    .Encode((value) => value),
  updatedAt: transformEpoch,
  updatedBy: Nullable(Type.String()),
  companyName: Type.String(),
  mustAgree: Type.Boolean(),
  agreedAt: transformEpoch,
});

const checker = TypeCompiler.Compile(schema);
export type PrivacyNotice = StaticDecode<typeof schema>;

const url = "/privacy-notice";
const queries = {
  fetch: queryOptions({
    queryKey: ["privacy-notice"] as const,
    queryFn: ({ signal }) => httpChecked.get(url, checker, { signal }),
  }),
};

export function usePrivacyNotice() {
  const { data } = useQuery({
    ...queries.fetch,
    // we cache the response forever, but let's refetch every 10 minutes just in
    // case it changes
    refetchInterval: 60_000 * 10,
    staleTime: Infinity,
  });

  return data;
}

export function useUpdatePrivacyNotice() {
  const { mutate } = useMutation({
    mutationFn: (body: Pick<PrivacyNotice, "enabled" | "notice">) =>
      http.post(url, { body }),
    onSettled: (_result, _error, _vars, _onMutateResult, { client }) =>
      client.invalidateQueries(queries.fetch),
  });

  return mutate;
}

const acceptSchema = ReadonlyObject({ returnTo: Type.String() });
const acceptChecker = TypeCompiler.Compile(acceptSchema);

export function useAcceptPrivacyNotice() {
  const { mutate } = useMutation({
    mutationFn: () =>
      httpChecked.post("/api/v1/privacy-notice-agreement", acceptChecker),
    onSuccess: (data) => redirectTo(consumeReturnTo() ?? data.returnTo),
  });

  return mutate;
}

export async function prefetchPrivacyNotice() {
  await queryClient
    .query({ ...queries.fetch, staleTime: Infinity })
    // eslint-disable-next-line unicorn/prefer-await
    .catch(noop);
}
